/**
 * Embedding themes: a whole look chosen by the embedder, on top of (and
 * instead of) the reference design and a customer's colours.
 *
 * Today there is one, `polaris`, for the embedded Shopify admin app: the
 * Seller Creator (in particular `/creator/guided`) should read as part of the
 * Shopify admin — Polaris type, radii, density and colours, no DesignDeck
 * wordmark. It is selected with `?embedTheme=polaris` on a Seller iframe
 * start (`isIframe=true` plus the signed credentials); lib/iframe-boot.ts
 * records it with the other iframe parameters, so it survives the auto-login
 * redirect, reloads and in-app navigation (e.g. to /designs after publish).
 *
 * Outside an iframe start the parameter is ignored: a top-level visitor never
 * gets the embedding look from a pasted URL. Without the parameter nothing
 * changes — the reference design stays exactly as the Shirtee Cloud sees it.
 *
 * The theme is a `data-embed-theme` attribute on <html>; the overrides live in
 * src/styles/embed-polaris.css. The customer's stored branding does not apply
 * under it (see lib/theme.ts `applyBranding`): the host's look wins.
 */

import { readIframeParams } from './iframe-boot';

export type EmbedTheme = 'polaris';

const EMBED_THEMES: readonly EmbedTheme[] = ['polaris'];
const ATTRIBUTE = 'embedTheme'; // dataset key of data-embed-theme

function asEmbedTheme(value: string | null | undefined): EmbedTheme | null {
  return EMBED_THEMES.find((theme) => theme === value) ?? null;
}

/**
 * The embedding theme of this page view: from the URL of an iframe start,
 * otherwise from the values recorded at the Seller iframe start.
 */
export function resolveEmbedTheme(): EmbedTheme | null {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  if (params.get('isIframe') === 'true') {
    return asEmbedTheme(params.get('embedTheme'));
  }
  return asEmbedTheme(readIframeParams().embedTheme);
}

/** Sets (or removes) `data-embed-theme` on <html>. Call once at boot. */
export function applyEmbedTheme(): EmbedTheme | null {
  if (typeof document === 'undefined') return null;
  const theme = resolveEmbedTheme();
  const { dataset } = document.documentElement;
  if (theme) dataset[ATTRIBUTE] = theme;
  else delete dataset[ATTRIBUTE];
  return theme;
}

/**
 * The embedding theme applied to this page view (the attribute set at boot),
 * or null. Layout differences are read through lib/embed-layout.ts, which
 * turns the theme into capabilities.
 */
export function currentEmbedTheme(): EmbedTheme | null {
  if (typeof document === 'undefined') return null;
  return asEmbedTheme(document.documentElement.dataset[ATTRIBUTE]);
}

/** True while the Polaris embedding theme is active on this page. */
export function isPolarisEmbed(): boolean {
  if (typeof document === 'undefined') return false;
  return document.documentElement.dataset[ATTRIBUTE] === 'polaris';
}
